import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, map, of, switchMap, tap } from 'rxjs';
import {
  GreetingType,
  MicrosoftTeamsCallQueue,
  MicrosoftTeamsCallQueueView,
  MusicOnHoldType,
  RoutingMethod,
} from '../../shared/models/generated/ziroModels';
import { UiAudioSettings } from './teams-call-queue-form/teams-call-queue-edit-form.component';
import { AudioFileResource } from '../../shared/resources/audio-file.resource';
import { MsTeamsCachedOptionsResource } from '../../admin/resources/ms-teams-cached-options.resource';
import { MicrosoftTeamsUserSearchResource } from '../../helpdesk/shared/resources/microsoft-teams-user-search.resource';
import { TeamsCallQueuesContext } from '../teams-call-queues.context';

export type ResourceAccountEntityArrayName = keyof Pick<
  MicrosoftTeamsCallQueue,
  'callingIdResourceAccountGuids' | 'resourceAccountGuids'
>;

export type ResourceAccountViewArrayName = keyof Pick<
  MicrosoftTeamsCallQueueView,
  'callingIdResourceAccounts' | 'resourceAccounts'
>;

export interface UiCallQueue extends MicrosoftTeamsCallQueue {
  audioSettings: UiAudioSettings;
}

export interface LanguageOption {
  label: string;
  value: string;
}

@Component({
  selector: 'ziro-teams-call-queue-edit',
  templateUrl: './teams-call-queue-edit.component.html',
  standalone: false,
})
export class TeamsCallQueueEditComponent implements OnInit {
  private _route = inject(ActivatedRoute);
  private _audioFileResource = inject(AudioFileResource);
  private _msTeamsCachedOptionsResource = inject(MsTeamsCachedOptionsResource);
  private _msTeamsUserSearchResource = inject(MicrosoftTeamsUserSearchResource);
  private _teamsCallQueuesContext = inject(TeamsCallQueuesContext);

  isLoading = true;
  callQueueView: MicrosoftTeamsCallQueueView;
  callQueueEntity: UiCallQueue;
  invalidResourceAccountOrAgent = false;
  languageOptions: LanguageOption[] = [];
  is360ViewChild = false;

  private _callQueueId: string;

  ngOnInit(): void {
    const { tenantId, callQueueId, upn } = this._route.snapshot.params;
    this.is360ViewChild = this._route.snapshot.data['is360ViewChild'];
    this._callQueueId = this._route.snapshot.params['callQueueId'];

    combineLatest([
      this._msTeamsCachedOptionsResource.getByTenantId(tenantId),
      this.is360ViewChild
        ? this._msTeamsUserSearchResource.search(tenantId, upn, null, 250, 'RESOURCE_ACCOUNT')
        : of(null),
    ])
      .pipe(
        switchMap(([teamsCachedOptions, resourceAccountArr]) => {
          const languageOptions: LanguageOption[] = (teamsCachedOptions?.teamsVoiceSupportedLanguages ?? []).map(
            (lang) => ({
              label: lang.displayName,
              value: lang.code,
            }),
          );

          this.languageOptions = languageOptions;

          const defaultLanguageCode = languageOptions.some((o) => o.value === 'en-US')
            ? 'en-US'
            : (languageOptions[0]?.value ?? '');

          if (!this._callQueueId) {
            this.callQueueView = {
              agentAlertTime: 15,
              allowOptOut: false,
              audioSettings: {
                greetingAudioId: '',
                greetingTtsMessage: '',
                greetingType: GreetingType.NONE,
                musicOnHoldAudioId: '',
                musicOnHoldType: MusicOnHoldType.NONE,
              },
              callQueueId: null,
              callingIdResourceAccounts: [],
              conferenceMode: false,
              groups: [],
              language: defaultLanguageCode,
              name: '',
              presenceAwareRouting: false,
              resourceAccounts: [],
              routingMethod: RoutingMethod.ATTENDANT,
              users: [],
              tenantId,
              microsoftTeamsTeams: null,
              teamSchedulingGroup: null,
              overflowSettings: null,
              timeoutSettings: null,
              noAgentSettings: null,
            };

            const {
              agentAlertTime,
              allowOptOut,
              audioSettings,
              callQueueId,
              callingIdResourceAccounts,
              conferenceMode,
              groups,
              language,
              name,
              presenceAwareRouting,
              resourceAccounts,
              routingMethod,
              users,
            } = this.callQueueView;

            this.callQueueEntity = {
              tenantId,
              agentAlertTime,
              allowOptOut,
              audioSettings,
              callQueueId,
              conferenceMode,
              language,
              name,
              presenceAwareRouting,
              routingMethod,
              callingIdResourceAccountGuids: callingIdResourceAccounts.map((ra) => ra.guid),
              resourceAccountGuids:
                !this.is360ViewChild && this._callQueueId
                  ? resourceAccounts.map((ra) => ra.guid)
                  : resourceAccountArr?.[0]?.guid
                    ? [resourceAccountArr?.[0]?.guid]
                    : [],
              groupGuids: groups.map((group) => group.guid),
              userGuids: users.map((user) => user.guid),
              teamSchedulingGroupId: '',
              microsoftTeamsTeamsId: '',
              overflowSettings: null,
              timeoutSettings: null,
              noAgentSettings: null,
            };

            return of(this.callQueueView);
          } else {
            return this._teamsCallQueuesContext.getCallQueue(callQueueId, tenantId).pipe(
              map((callQueue) => {
                this.callQueueView = callQueue;

                const {
                  agentAlertTime,
                  allowOptOut,
                  audioSettings,
                  callQueueId,
                  callingIdResourceAccounts,
                  conferenceMode,
                  groups,
                  language,
                  name,
                  presenceAwareRouting,
                  resourceAccounts,
                  routingMethod,
                  users,
                } = this.callQueueView;

                this.callQueueEntity = {
                  tenantId,
                  agentAlertTime,
                  allowOptOut,
                  audioSettings,
                  callQueueId,
                  conferenceMode,
                  language,
                  name,
                  presenceAwareRouting,
                  routingMethod,
                  callingIdResourceAccountGuids: callingIdResourceAccounts.map((ra) => ra.guid),
                  resourceAccountGuids: resourceAccounts.map((ra) => ra.guid),
                  groupGuids: groups.map((group) => group.guid),
                  userGuids: users.map((user) => user.guid),
                  teamSchedulingGroupId: '',
                  microsoftTeamsTeamsId: '',
                  overflowSettings: null,
                  timeoutSettings: null,
                  noAgentSettings: null,
                };

                return this.callQueueView;
              }),
            );
          }
        }),
        switchMap((callQueueView) => {
          const {
            audioSettings: { greetingAudioId, musicOnHoldAudioId },
          } = callQueueView;

          if (greetingAudioId && musicOnHoldAudioId) {
            return combineLatest([
              this._audioFileResource.getAudioFile(tenantId, 'call-queues', greetingAudioId),
              this._audioFileResource.getAudioFile(tenantId, 'call-queues', musicOnHoldAudioId),
            ]).pipe(
              tap(([greetingAudio, musicOnHoldAudio]) => {
                this.callQueueEntity = {
                  ...this.callQueueEntity,
                  audioSettings: {
                    ...this.callQueueEntity.audioSettings,
                    greetingUpload: greetingAudio,
                    musicOnHoldUpload: musicOnHoldAudio,
                  },
                };
              }),
            );
          } else if (greetingAudioId) {
            return this._audioFileResource.getAudioFile(tenantId, 'call-queues', greetingAudioId).pipe(
              tap(
                (greetingAudio) =>
                  (this.callQueueEntity = {
                    ...this.callQueueEntity,
                    audioSettings: {
                      ...this.callQueueEntity.audioSettings,
                      greetingUpload: greetingAudio,
                    },
                  }),
              ),
            );
          } else if (musicOnHoldAudioId) {
            return this._audioFileResource.getAudioFile(tenantId, 'call-queues', musicOnHoldAudioId).pipe(
              tap(
                (musicOnHoldAudio) =>
                  (this.callQueueEntity = {
                    ...this.callQueueEntity,
                    audioSettings: {
                      ...this.callQueueEntity.audioSettings,
                      musicOnHoldUpload: musicOnHoldAudio,
                    },
                  }),
              ),
            );
          } else {
            return of(null);
          }
        }),
      )
      .subscribe(() => (this.isLoading = false));
  }

  setInvalidResourceAccountOrAgent(isQueueValid: boolean): void {
    this.invalidResourceAccountOrAgent = !isQueueValid;
  }
}
